import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSeller } from '../../context/SellerContext';
import StepIndicator from '../StepIndicator';
import MobileVerification from './MobileVerification';
import BusinessDetailsForm from './BusinessDetailsForm';
import PickupAddressForm from './PickupAddressForm';
import BankDetailsForm from './BankDetailsForm';
import SupplierDetailsForm from './SupplierDetailsForm';
import SuccessModal from './SuccessModal';
import '../../styles/SellerRegistration.css';

const STEP_TITLES = [
  'Mobile Verification',
  'Business Details',
  'Pickup Address',
  'Bank Details',
  'Supplier Details',
];

const SellerRegistrationLayout = () => {
  const {
    currentStep,
    nextStep,
    prevStep,
    authToken,
    seller,
  } = useSeller();

  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);

  // Redirect to dashboard if seller is already verified
  useEffect(() => {
    if (authToken && seller?.isVerified) {
      navigate('/seller/dashboard', { replace: true });
    }
  }, [authToken, seller, navigate]);

  if (showSuccess) {
    return <SuccessModal />;
  }

  // Landing Page Layout (Step 1)
  if (currentStep === 1) {
    return (
      <div className="seller-reg-landing">
        {/* Landing Header */}
        <div className="seller-header-wrapper">
          <div className="seller-header-content">
            <div className="seller-logo">Kalinga Crafts Store</div>
            <div className="seller-login-link-container">
              <span className="seller-login-text">Already a user?</span>
              <Link
                to="/seller/login"
                className="seller-login-btn"
              >
                Login
              </Link>
            </div>
          </div>
        </div>

        {/* Landing Content */}
        <div className="seller-main-content">
          <MobileVerification onNext={nextStep} />
        </div>
      </div>
    );
  }

  // Wizard Layout (Steps 2-5)
  return (
    <div className="seller-reg-container">
      {/* Header */}
      <div className="wizard-header-wrapper">
        <div className="wizard-header-content">
          <div className="wizard-header-top">
            <div className="wizard-logo">Kalinga Crafts Store</div>
            <div className="wizard-step-count">
              Step {currentStep} of {STEP_TITLES.length}
            </div>
          </div>
          <StepIndicator
            currentStep={currentStep}
            totalSteps={STEP_TITLES.length}
            stepTitles={STEP_TITLES}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="seller-main-content">
        <div className="seller-grid-layout">
          {/* Form Section */}
          <div>
            <div className="seller-form-container">
              {currentStep === 2 && (
                <BusinessDetailsForm onNext={nextStep} onPrev={prevStep} />
              )}

              {currentStep === 3 && (
                <PickupAddressForm onNext={nextStep} onPrev={prevStep} />
              )}

              {currentStep === 4 && (
                <BankDetailsForm onNext={nextStep} onPrev={prevStep} />
              )}

              {currentStep === 5 && (
                <SupplierDetailsForm
                  onNext={nextStep}
                  onPrev={prevStep}
                  onSuccess={() => setShowSuccess(true)}
                />
              )}
            </div>
          </div>

          {/* Sidebar - Info Section */}
          <div>
            <div className="seller-info-sidebar">
              <h3 className="seller-info-title">
                Why Sell on Kalinga Crafts Store?
              </h3>

              <div className="seller-benefit-list">
                <div className="seller-benefit-item">
                  <div className="seller-benefit-icon">
                    <svg style={{ height: '24px', width: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="seller-benefit-text">
                    <p className="seller-benefit-title">Zero Commission</p>
                    <p className="seller-benefit-desc">Sell without any commission charges</p>
                  </div>
                </div>

                <div className="seller-benefit-item">
                  <div className="seller-benefit-icon">
                    <svg style={{ height: '24px', width: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="seller-benefit-text">
                    <p className="seller-benefit-title">Free Shipping</p>
                    <p className="seller-benefit-desc">To 24,000+ pincodes across India</p>
                  </div>
                </div>

                <div className="seller-benefit-item">
                  <div className="seller-benefit-icon">
                    <svg style={{ height: '24px', width: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="seller-benefit-text">
                    <p className="seller-benefit-title">6 lakh+ Suppliers</p>
                    <p className="seller-benefit-desc">Join our growing community</p>
                  </div>
                </div>

                <div className="seller-benefit-item">
                  <div className="seller-benefit-icon">
                    <svg style={{ height: '24px', width: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="seller-benefit-text">
                    <p className="seller-benefit-title">Easy Setup</p>
                    <p className="seller-benefit-desc">Register in just 5 minutes</p>
                  </div>
                </div>
              </div>

              <div className="seller-privacy-footer">
                <p className="seller-privacy-text">
                  Your information is secure and protected by our{' '}
                  <a href="/privacy" className="seller-privacy-link">
                    privacy policy
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerRegistrationLayout;
